import { ReactNode, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { BatchProgress } from '@components/batch-progress';
import { ButtonSecondary } from '@components/button';
import { CardDisclosure } from '@components/card-disclosure';
import { CRITICAL } from '@components/card-disclosure/src/card-disclosure.type';
import { createRowErrors } from '@components/forms/utils/create-row-errors';
import { IconCritical } from '@components/icons';
import { EVENT_LIST, EVENT_REPORT_ROUTE } from '@constants/routes';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { LAYOUT_WITHOUT_SIDEBAR } from '@contexts/layout/layout.type';
import { LayoutContext } from '@contexts/layout/layout-context';
import { NavigationContext } from '@contexts/navigation/navigation-context';
import { useGetEvent } from '@hooks/events/use-get-event';
import { useGetImportStatus } from '@hooks/import/use-get-import-status';
import { VIEW_UPCOMING } from '@hooks/use-event-navigation/use-event-navigation';
import { PENDING_IMPORT_ID } from '@pages/events/utils/import-process-utils';

import { buildProcessLiterals } from './import-process-literals';
import { useOptimisticCsvImport } from './use-optimistic-csv-import';

const StatusMessage = ({
  isSubmitError,
  isActive,
  errorMessage,
  loadingContent,
  finishedContent,
}: {
  isSubmitError: boolean;
  isActive: boolean;
  errorMessage: string;
  loadingContent: ReactNode;
  finishedContent: ReactNode;
}) => {
  if (isSubmitError) return <>{errorMessage}</>;
  if (isActive) return <>{loadingContent}</>;
  return <>{finishedContent}</>;
};

const ImportProcess = () => {
  const {
    eventId,
    importId: importIdParam,
    importType,
  } = useParams<{ eventId: string; importId: string; importType: string }>();
  const navigate = useNavigate();
  const { data: eventData } = useGetEvent({ eventId, options: { enabled: !!eventId } });
  const [isLoading, setIsLoading] = useState(true);
  const {
    route: { from: backRoute, to: toRoute },
  } = useContext(NavigationContext);
  const { setLayout } = useContext(LayoutContext);
  const {
    clubState: { slug: clubSlug },
    companyState: { legacyId: companyLegacyId },
  } = useContext(CompanyClubContext);
  const { t } = useTranslation();

  const { resolvedImportId, isSubmittingPhase, isSubmitError } = useOptimisticCsvImport(importIdParam, eventId);

  const { data: importCsvStatusData } = useGetImportStatus({
    importId: resolvedImportId,
    options: { enabled: isLoading && !!resolvedImportId, refetchInterval: 1000 },
  });

  const totalRowsProcessed = importCsvStatusData
    ? importCsvStatusData.pending + importCsvStatusData.completed + importCsvStatusData.failed.quantity
    : 0;

  const sortedRowErrors =
    importCsvStatusData?.failed &&
    importCsvStatusData.failed.quantity > 0 &&
    [...importCsvStatusData.failed.rows].sort((a, b) => a - b);

  const isActive = isLoading || isSubmittingPhase;
  const processesLiterals = buildProcessLiterals(importCsvStatusData, isActive, t);

  const navigateBack = () => {
    const isBackRouteImportPage = backRoute?.includes(`/import/${PENDING_IMPORT_ID}/`);
    if (backRoute && backRoute !== toRoute && !isBackRouteImportPage) {
      navigate(backRoute);
      return;
    }
    if (eventId) {
      const path = EVENT_REPORT_ROUTE.replace(':companyId', companyLegacyId)
        .replace(':clubSlug', clubSlug)
        .replace(':eventId', eventId);
      navigate(path);
      return;
    }
    const path = EVENT_LIST.replace(':companyId', companyLegacyId)
      .replace(':clubSlug', clubSlug)
      .replace(':view', VIEW_UPCOMING);
    navigate(path);
  };

  useEffect(() => {
    if (!importCsvStatusData) return;
    if (importCsvStatusData.pending === 0) {
      setIsLoading(false);
    }
  }, [importCsvStatusData]);

  useEffect(() => setLayout({ type: LAYOUT_WITHOUT_SIDEBAR }), [setLayout]);

  return (
    <div className='flex flex-col items-center justify-center w-full gap-8 px-4 sm:px-8 min-h-screen overflow-auto'>
      <div className='flex flex-col items-center justify-center max-w-[415px] mx-auto gap-8 pt-8 sm:mx-8'>
        <header className='text-center'>
          {isSubmitError && <IconCritical className='w-9 h-9 mx-auto mb-6 text-critical-no-bg' />}
          <h1 className='text-headline-l md:text-headline-xl mb-4 text-primary'>
            {isSubmitError
              ? t('common:oopsSeemsLikeSomethingWentWrong')
              : processesLiterals[importType as string]?.title ?? ''}
          </h1>
          <p className='text-secondary'>
            <StatusMessage
              isSubmitError={isSubmitError}
              isActive={isActive}
              errorMessage={t('errors:genericError')}
              loadingContent={
                <Trans i18nKey='common:processImportInfoLoading' values={{ name: eventData?.name }}>
                  <br />
                  <span className='text-subtitle-m text-primary'>{eventData?.name}</span>
                  <br />
                </Trans>
              }
              finishedContent={
                <Trans
                  i18nKey='common:processImportInfoFinished'
                  values={{
                    name: eventData?.name,
                    extraLines: processesLiterals[importType as string]?.quantityFailed ?? '',
                  }}>
                  We have imported the bookings into the event
                  <span className='text-subtitle-m text-primary' />
                  <span />
                </Trans>
              }
            />
          </p>
        </header>
        {!isSubmitError && (
          <section className='w-full'>
            <BatchProgress
              completed={importCsvStatusData?.completed ?? 0}
              total={totalRowsProcessed}
              isFinished={!isActive}
            />
          </section>
        )}
        {sortedRowErrors && (
          <section className='w-full -mt-4'>
            <CardDisclosure
              titleText={t('common:processImportCsvTitleErrors', { rows: importCsvStatusData?.failed?.quantity })}
              descriptionText={`${t('common:rows')}: ${createRowErrors(sortedRowErrors)}`}
              showChevronIcon
              variant={CRITICAL}
              isDefaultOpen={sortedRowErrors.length < 30}
            />
          </section>
        )}
        <footer className='flex items-center flex-col'>
          <ButtonSecondary onClick={navigateBack} className='mb-4 ' disabled={isSubmittingPhase && !isSubmitError}>
            {t('common:doneButton')}
          </ButtonSecondary>
          {isActive && !isSubmitError && (
            <p className='text-center text-tertiary text-body-xs]'>{t('common:processImportInfoText')}</p>
          )}
        </footer>
      </div>
    </div>
  );
};

export default ImportProcess;
